// Error + telemetry reporter. Two responsibilities:
//   1. Capture uncaught errors / promise rejections as telemetry events.
//   2. Periodically flush the telemetry buffer to POST /api/telemetry so
//      events are persisted server-side (the audit flagged the bus was
//      console-only with no remote sink).
//
// Sentry is optional and dependency-free here: the app ships no Sentry
// SDK. If an operator loads the Sentry browser bundle (script tag /
// their own bundling) so `window.Sentry` exists, captured errors are
// forwarded to it. Otherwise the self-hosted /api/telemetry sink is the
// error store — functional with zero external account.
import { telemetry } from './bus';
import type { TelemetryEvent } from './schema';

declare global {
  interface Window {
    Sentry?: { captureException?: (e: unknown) => void; captureMessage?: (m: string) => void };
  }
}

function forwardToSentry(message: string, err?: unknown) {
  const s = window.Sentry;
  if (!s) return;
  if (err && s.captureException) s.captureException(err);
  else if (s.captureMessage) s.captureMessage(message);
}

const FLUSH_INTERVAL_MS = 8000;
const FLUSH_THRESHOLD = 40;

let pending: TelemetryEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;

async function flush(useBeacon = false) {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  const body = JSON.stringify({ events: batch });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon('/api/telemetry', new Blob([body], { type: 'application/json' }));
      return;
    }
    await fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Network failure — drop the batch rather than growing unbounded.
    // (Telemetry is best-effort; the in-memory bus still has the tail.)
  }
}

export function installReporter() {
  // 1. Crash capture → telemetry sink + optional Sentry forward.
  window.addEventListener('error', (e) => {
    telemetry.emit({ type: 'error.uncaught', ts: performance.now(), message: String(e.message), source: e.filename });
    forwardToSentry(String(e.message), e.error);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    telemetry.emit({ type: 'error.rejection', ts: performance.now(), message: String(reason?.message ?? reason) });
    forwardToSentry(String(reason?.message ?? reason), reason);
  });

  // 2. Remote sink: buffer every emitted event, flush on interval /
  // threshold / page hide.
  telemetry.subscribe((e) => {
    pending.push(e);
    if (pending.length >= FLUSH_THRESHOLD) void flush();
  });
  timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => void flush(true));
}

export function stopReporter() {
  if (timer) clearInterval(timer);
  timer = null;
}
